import React from "react";
import {Box, MenuItem, Switch, TextField, Tooltip, Typography} from "@mui/material";

import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";
import {AxisId, AxisSettings, ChartSpec, SeriesSettings, axisSettingsOf, drawnAs,
        seriesSettingsOf, usesSecondaryAxis} from "./chartSpec";
import {ResultColumn} from "./reportApi";

/**
 * The axes and the series, tuned one at a time.
 *
 * SEPARATE FROM ChartBuilder BECAUSE IT IS A DIFFERENT QUESTION. The shelves answer "what is this
 * chart OF"; this answers "how should it look" -- and the shelves have to stay the first thing a
 * reader meets. A chart with nothing on its Values shelf has nothing to tune, so this renders
 * nothing at all rather than a panel of controls addressing no series.
 *
 * ONLY FOR CHARTS THAT DECLARE `tunable`, which is bar and line. A pie has no axes, a scatter is
 * one series by construction, and a heatmap's third dimension is colour rather than a scale --
 * offering an axis panel on any of them would be controls that change nothing.
 *
 * EVERY CONTROL HERE CAN BE EMPTY, and empty is not zero. A blank Minimum means "from the data",
 * which is what a chart does before anybody touches it; typing 0 and clearing it again has to get
 * back to that state rather than pinning the floor at zero for ever.
 */

type Props = {
    spec: ChartSpec;
    columns: ResultColumn[];
    onChange: (spec: ChartSpec) => void;
};

/** The colours a series may be given by name, beyond the palette's own order.
 *
 *  A SHORT LIST RATHER THAN A COLOUR WHEEL. Eight hues that hold up beside each other is a
 *  decision worth making once; a free picker is how a chart ends up with two greens nobody can
 *  tell apart, and neither this app nor the person using it wants to relitigate contrast. */
export const SERIES_COLOURS: {id: string; label: string}[] = [
    {id: "#1498D8", label: "Blue"},
    {id: "#0575AD", label: "Deep blue"},
    {id: "#00A044", label: "Green"},
    {id: "#F1C40F", label: "Yellow"},
    {id: "#E67E22", label: "Orange"},
    {id: "#E74C3C", label: "Red"},
    {id: "#8E44AD", label: "Purple"},
    {id: "#525968", label: "Grey"},
];

const LABEL = {fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em", color: Blue3, mb: 0.75};
const CARD = {border: "1px solid #E4E7EC", borderRadius: "4px", p: 1.5};

/** A number field that keeps "" distinct from 0 -- see the note at the top. */
function NumberField({label, value, onChange, width = 104, step}:
                     {label: string; value: number | undefined;
                      onChange: (value: number | undefined) => void; width?: number;
                      step?: number}) {
    return (
        <TextField size="small" type="number" label={label} sx={{width}}
                   value={value === undefined ? "" : value}
                   slotProps={{htmlInput: {"aria-label": label, step}}}
                   onChange={event => {
                       const text = event.target.value;
                       onChange(text === "" ? undefined : Number(text));
                   }}/>
    );
}

/** On, off, or "whatever the chart says" -- which a switch cannot express.
 *
 *  A SWITCH WAS WRONG HERE and briefly shipped that way: it could turn an override ON but never
 *  explicitly OFF, so a series could not opt out of a chart-level setting at all. The third state
 *  is not a nicety; it is the difference between "I have not decided" and "no". */
function Inherit({label, value, onChange, width = 116}:
                 {label: string; value: boolean | undefined;
                  onChange: (value: boolean | undefined) => void; width?: number}) {
    return (
        <TextField select size="small" label={label} sx={{width}}
                   value={value === undefined ? "" : value ? "on" : "off"}
                   onChange={event => onChange(event.target.value === ""
                       ? undefined : event.target.value === "on")}>
            <MenuItem value="">As the chart</MenuItem>
            <MenuItem value="on">On</MenuItem>
            <MenuItem value="off">Off</MenuItem>
        </TextField>
    );
}

export default function ChartTuning({spec, columns, onChange}: Props) {
    const keys = spec.encodings.values ?? [];
    if (keys.length === 0) return null;

    const setSeries = (key: string, over: Partial<SeriesSettings>) => {
        const next = {...seriesSettingsOf(spec, key), ...over};
        // AN EMPTY SETTING IS REMOVED, not stored as undefined. A specification is saved as JSON
        // and read back by a renderer that asks "is this absent?" -- so clearing a colour has to
        // leave no key behind, or the spec grows a record of every control ever touched.
        for (const field of Object.keys(next) as (keyof SeriesSettings)[]) {
            if (next[field] === undefined) delete next[field];
        }
        const series = {...(spec.series ?? {}), [key]: next};
        if (Object.keys(next).length === 0) delete series[key];
        onChange({...spec, series});
    };

    const setAxis = (id: AxisId, over: Partial<AxisSettings>) => {
        const next = {...axisSettingsOf(spec, id), ...over};
        for (const field of Object.keys(next) as (keyof AxisSettings)[]) {
            if (next[field] === undefined || next[field] === "" || next[field] === false) {
                delete next[field];
            }
        }
        const axes = {...(spec.axes ?? {}), [id]: next};
        if (Object.keys(next).length === 0) delete axes[id];
        onChange({...spec, axes});
    };

    const secondary = usesSecondaryAxis(spec, keys);

    // MUI HAS NO HORIZONTAL LINE SERIES, so a bar chart laid on its side is all bars whatever
    // these say. The control is greyed with the reason rather than hidden, and the SETTING IS
    // KEPT -- standing the chart up again brings the combination back.
    const sideways = spec.kind === "bar" && !!spec.options.horizontal;

    return (
        <Box sx={{mt: 2, pt: 2, borderTop: "1px solid #F1F4F7"}}>

            {/* ---- the series ------------------------------------------------------------- */}
            <Typography sx={LABEL}>SERIES</Typography>
            <Box sx={{display: "grid", gap: 1, mb: 2.5}}>
                {keys.map(key => {
                    const column = columns.find(one => one.Key === key);
                    const own = seriesSettingsOf(spec, key);
                    const name = column?.Label ?? key;
                    return (
                        // A NAMED GROUP PER MEASURE. Six copies of "Measured against" on one
                        // panel is six controls a screen reader cannot tell apart, and the row's
                        // heading is the only thing that distinguishes them -- so the row carries
                        // the name and the controls keep their plain labels.
                        <Box key={key} role="group" aria-label={name}
                             sx={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
                                  ...CARD}}>
                            <Typography sx={{fontSize: 13, color: Grey17, minWidth: 118,
                                             fontWeight: 500}}>
                                {name}
                            </Typography>

                            {/* THE SECOND AXIS, WHICH IS WHY THIS PANEL EXISTS. Four thousand
                                orders and two million dollars on one scale draws the orders as a
                                flat line along the floor. */}
                            <TextField select size="small" label="Measured against" sx={{width: 156}}
                                       value={own.axis ?? "primary"}
                                       onChange={event => setSeries(key,
                                           {axis: event.target.value === "secondary"
                                                  ? "secondary" : undefined})}>
                                <MenuItem value="primary">The main axis</MenuItem>
                                <MenuItem value="secondary">A second axis</MenuItem>
                            </TextField>

                            {/* BARS OR A LINE, PER MEASURE -- which is what makes a combination
                                chart. Volume as bars with a rate drawn over them is the shape
                                people mean by one, and the different MARK says "these are
                                different kinds of thing" before the reader has read either axis. */}
                            <Tooltip title={sideways
                                            ? "A chart laid on its side has no lines to draw: every measure is a bar until you stand it up again."
                                            : ""}>
                                <span>
                                    <TextField select size="small" label="Draw as" sx={{width: 120}}
                                               disabled={sideways}
                                               value={sideways ? "bar" : (own.draw ?? "")}
                                               onChange={event => setSeries(key,
                                                   {draw: (event.target.value || undefined) as
                                                          SeriesSettings["draw"]})}>
                                        <MenuItem value="">As the chart</MenuItem>
                                        <MenuItem value="bar">Bars</MenuItem>
                                        <MenuItem value="line">A line</MenuItem>
                                    </TextField>
                                </span>
                            </Tooltip>

                            <TextField select size="small" label="Colour" sx={{width: 132}}
                                       value={own.color ?? ""}
                                       onChange={event => setSeries(key,
                                           {color: event.target.value || undefined})}>
                                <MenuItem value="">Automatic</MenuItem>
                                {SERIES_COLOURS.map(one => (
                                    <MenuItem key={one.id} value={one.id}>
                                        <Box component="span" sx={{display: "inline-block",
                                                                   width: 10, height: 10, mr: 1,
                                                                   borderRadius: "2px",
                                                                   backgroundColor: one.id}}/>
                                        {one.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* A LINE'S OWN CONTROLS FOLLOW WHAT IT IS DRAWN AS. A measure made
                                into a line on a bar chart is a line, and would otherwise be the
                                only one in the app with no shape and no marks. */}
                            {drawnAs(spec, key, sideways) === "line" ? (
                                <>
                                    <TextField select size="small" label="Shape" sx={{width: 120}}
                                               value={own.curve ?? ""}
                                               onChange={event => setSeries(key,
                                                   {curve: event.target.value || undefined})}>
                                        <MenuItem value="">As the chart</MenuItem>
                                        <MenuItem value="monotone">Curved</MenuItem>
                                        <MenuItem value="linear">Straight</MenuItem>
                                        <MenuItem value="step">Stepped</MenuItem>
                                    </TextField>
                                    {/* THE SAME THREE SETTINGS THE CHART HAS, per line. Ed could
                                        reach Shape and nothing else: fill and gap-joining were
                                        chart-level only, so two lines on one chart could not
                                        differ in the ways that make them tellable apart. */}
                                    <Inherit label="Marks" value={own.showMark}
                                             onChange={showMark => setSeries(key, {showMark})}/>
                                    <Inherit label="Fill" value={own.area}
                                             onChange={area => setSeries(key, {area})}/>
                                    <Inherit label="Join gaps" value={own.connectNulls}
                                             onChange={connectNulls =>
                                                 setSeries(key, {connectNulls})}/>
                                </>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>

            {/* ---- the axes --------------------------------------------------------------- */}
            <Typography sx={LABEL}>AXES</Typography>
            <Box sx={{display: "grid", gap: 1.5,
                      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))"}}>

                <ValueAxisCard title={secondary ? "Main axis" : "Value axis"}
                               settings={axisSettingsOf(spec, "primary")}
                               onChange={over => setAxis("primary", over)}/>

                {/* ONLY WHEN SOMETHING IS ON IT. An axis nothing is measured against is a labelled
                    line down the side of the chart, and a card here for an axis that is not drawn
                    is a set of controls with no visible effect. */}
                {secondary ? (
                    <ValueAxisCard title="Second axis"
                                   settings={axisSettingsOf(spec, "secondary")}
                                   onChange={over => setAxis("secondary", over)}/>
                ) : null}

                <Box role="group" aria-label="Category axis" sx={CARD}>
                    <Typography sx={{fontSize: 12.5, fontWeight: 600, color: Grey17, mb: 1}}>
                        Category axis
                    </Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center"}}>
                        <TextField size="small" label="Title" sx={{width: 168}}
                                   value={axisSettingsOf(spec, "category").label ?? ""}
                                   onChange={event => setAxis("category",
                                       {label: event.target.value || undefined})}/>
                        {/* WHAT SAVES FORTY LONG LABELS FROM OVERLAPPING. MUI drops labels it
                            cannot fit rather than tilting them, so a chart of department names
                            silently shows every third one until this is turned up. */}
                        <TextField select size="small" label="Label angle" sx={{width: 132}}
                                   value={String(axisSettingsOf(spec, "category").tickAngle ?? 0)}
                                   onChange={event => setAxis("category",
                                       {tickAngle: Number(event.target.value) || undefined})}>
                            <MenuItem value="0">Flat</MenuItem>
                            <MenuItem value="-30">Tilted</MenuItem>
                            <MenuItem value="-45">Steep</MenuItem>
                            <MenuItem value="-90">Upright</MenuItem>
                        </TextField>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Switch size="small"
                                    checked={!!axisSettingsOf(spec, "category").reverse}
                                    slotProps={{input: {"aria-label": "Reverse"}}}
                                    onChange={event => setAxis("category",
                                        {reverse: event.target.checked})}/>
                            <Typography sx={{fontSize: 12.5, color: Grey6}}>Reverse</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function ValueAxisCard({title, settings, onChange}:
                       {title: string; settings: AxisSettings;
                        onChange: (over: Partial<AxisSettings>) => void}) {
    return (
        // THE CARD IS THE NAME, so its fields can be called Title, Minimum and Maximum. Spelling
        // the axis into each label made them "Value axi…" at the width they have -- a label
        // truncated to the word every one of them shares.
        <Box role="group" aria-label={title} sx={CARD}>
            <Typography sx={{fontSize: 12.5, fontWeight: 600, color: Grey17, mb: 1}}>
                {title}
            </Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center"}}>
                <TextField size="small" label="Title" sx={{width: 168}}
                           value={settings.label ?? ""}
                           onChange={event => onChange({label: event.target.value || undefined})}/>

                <NumberField label="Minimum" value={settings.min} onChange={min => onChange({min})}/>
                <NumberField label="Maximum" value={settings.max} onChange={max => onChange({max})}/>

                <Tooltip title="A logarithmic or square-root axis cannot show zero or a negative value, so it is ignored on data that has one rather than dropping the point.">
                    <TextField select size="small" label="Scale" sx={{width: 140}}
                               value={settings.scale ?? "linear"}
                               onChange={event => onChange(
                                   {scale: event.target.value === "linear"
                                           ? undefined : event.target.value as AxisSettings["scale"]})}>
                        <MenuItem value="linear">Even</MenuItem>
                        <MenuItem value="log">Logarithmic</MenuItem>
                        <MenuItem value="sqrt">Square root</MenuItem>
                    </TextField>
                </Tooltip>

                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <Switch size="small" checked={!!settings.reverse}
                            slotProps={{input: {"aria-label": "Reverse"}}}
                            onChange={event => onChange({reverse: event.target.checked})}/>
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>Reverse</Typography>
                </Box>
            </Box>
            <Typography sx={{fontSize: 11.5, color: Grey5, mt: 1}}>
                Leave the minimum and maximum blank to take them from the data.
            </Typography>
        </Box>
    );
}
